/** Small form pieces the Work drawers and modals share. */
import type { ComponentType, ReactNode } from "react";
import { Avatar } from "@/components/ui";
import { cn } from "@/lib/utils";
import { firstName, dotClass } from "@/features/command/ui";
import type { Person } from "./types";

export function Field({
  label, icon: Icon, hint, children, className,
}: { label: ReactNode; icon?: ComponentType<{ className?: string }>; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-400">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" />}
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] leading-relaxed text-zinc-500">{hint}</div>}
    </div>
  );
}

/** A row of choice chips ("Growth · Customers · …"). Click the chosen one again to clear it when `clearable`. */
export function ChoicePills<T extends string>({
  options, value, onChange, clearable, size = "md",
}: {
  options: Array<{ value: T; label: ReactNode; color?: string | null }>;
  value: T | null | undefined;
  onChange: (v: T | null) => void;
  clearable?: boolean;
  size?: "sm" | "md";
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(on && clearable ? null : o.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors",
              size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
              on
                ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-100"
                : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700 hover:text-zinc-100",
            )}
          >
            {o.color && <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(o.color))} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pick the person who owns something. Two founders today, so chips beat a dropdown. */
export function PersonPills({
  people, value, onChange, allowNone,
}: { people: Person[]; value: string | null | undefined; onChange: (id: string | null) => void; allowNone?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {people.map((p) => {
        const on = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-xs font-medium transition-colors",
              on ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-100" : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700 hover:text-zinc-100",
            )}
          >
            <Avatar name={p.name} email={p.email} src={p.image} size="xs" />
            {firstName(p.name)}
          </button>
        );
      })}
      {allowNone && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            !value ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-100" : "border-zinc-800 bg-zinc-900/50 text-zinc-500 hover:text-zinc-200",
          )}
        >
          Nobody yet
        </button>
      )}
    </div>
  );
}

/** Weekday toggles, Monday first. */
export function DayToggles({ value, onChange }: { value: number[]; onChange: (days: number[]) => void }) {
  const order = [1, 2, 3, 4, 5, 6, 0];
  const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="flex flex-wrap gap-1">
      {order.map((d) => {
        const on = value.includes(d);
        return (
          <button
            key={d}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d].sort())}
            className={cn(
              "h-8 w-11 rounded-lg border text-xs font-medium transition-colors",
              on ? "border-indigo-400/50 bg-indigo-500/20 text-indigo-100" : "border-zinc-800 bg-zinc-900/50 text-zinc-500 hover:text-zinc-200",
            )}
          >
            {names[d]}
          </button>
        );
      })}
    </div>
  );
}

/** A native time box styled like our inputs. Empty = no time. */
export function TimeInput({ value, onChange, className, placeholder }: { value: string; onChange: (v: string) => void; className?: string; placeholder?: string }) {
  return (
    <input
      type="time"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "h-9 rounded-lg border border-zinc-800 bg-zinc-900/50 px-2.5 text-sm text-zinc-100 [color-scheme:dark] focus:border-indigo-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
        "[html.light_&]:[color-scheme:light]",
        className,
      )}
    />
  );
}
